import React, { useEffect, useState } from 'react';
import { db, auth } from '../firebase-config';
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from 'react-router-dom';
import "../style/Profile.css"

function Profile() {
    const [userData, setUserData] = useState(null);
    const [isHover, setIsHover] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                const user = auth.currentUser;
                if (user) {
                    const userDoc = await getDoc(doc(db, "Users", user.uid));
                    if (userDoc.exists()) {
                        const userData = userDoc.data();
    
                        const joinedClasses = userData.joinedClasses || [];
                        const createdClasses = userData.classesCreated || [];
    
                        // Collect all classIds to check
                        const classIdsToCheck = [
                            ...joinedClasses.map(c => c.classId),
                            ...createdClasses.map(c => c.classId)
                        ].filter(Boolean);
    
                        const classStatusMap = {};
    
                        // Fetch all class docs to check isArchived
                        for (const classId of classIdsToCheck) {
                            const classDoc = await getDoc(doc(db, "Classes", classId));
                            if (classDoc.exists()) {
                                const classData = classDoc.data();
                                classStatusMap[classId] = classData.isArchived || false;
                            }
                        }
    
                        // Filter out archived
                        const filteredJoined = joinedClasses.filter(c => !classStatusMap[c.classId]);
                        const filteredCreated = createdClasses.filter(c => !classStatusMap[c.classId]);
    
                        setUserData({
                            ...userData,
                            joinedClasses: filteredJoined,
                            classesCreated: filteredCreated
                        });
                    }
                }
            } catch (err) {
                console.error("Error fetching user data: ", err);
            }
        };
        fetchUserData();
    }, []);
    
    

    const handleEditClick = () => {
        navigate('/EditProfile');
    };

    const handleClassClick = (cls) => {
        if (!cls?.classId) return;
      
        // Store selected class to session or context if needed
        sessionStorage.setItem('selectedClassId', cls.classId);
        sessionStorage.setItem('selectedClassName', cls.className || cls.name);
      
        if (userData.role === 'Teacher') {
            navigate('/TeacherDashboard', { state: { classId: cls.classId } });   
        } else {
            navigate('/StudentDashboard', { state: { classId: cls.classId } });
        }
        };
      
    const capitalise = (str) => {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };
    
    return (
        <div className="profile-container">
            {userData ? (
                <div className="profile-wrapper">
                    {/* Profile Header */}
                    <div className="profile-header">
                        <div className="cover-photo" style={{ backgroundImage: userData.coverPhotoUrl ? `url(${userData.coverPhotoUrl})` : 'none' }} />

                        <div className="profile-info">
                            <div className={`profile-picture-wrapper ${userData.profilePicUrl ? 'has-image' : ''}`}>
                            {userData.profilePicUrl ? (
                                <img src={userData.profilePicUrl} alt="Profile" className="profile-picture" />
                            ) : (
                                <span className="initials">
                                {userData.firstName?.charAt(0) || ''}{userData.lastName?.charAt(0) || ''}
                                </span>
                            )}
                            </div>

                            <div className="profile-text">
                            <h2 className="profile-name">{capitalise(userData.firstName)} {capitalise(userData.lastName)}</h2>
                            {userData.bio && <p className="profile-bio">{userData.bio}</p>}
                            {userData.showPreferredName && userData.preferredName ? (
                                <p className="profile-label">Preferred Name: {userData.preferredName}</p>
                            ) : (
                                <p className="profile-label">&nbsp;</p> // Blank placeholder
                            )}
                            {userData.showDegree && userData.degree ? (
                                <p className="profile-label">Degree: {userData.degree}</p>
                            ) : (
                                <p className="profile-label">&nbsp;</p> // Blank placeholder
                            )}
                            </div>

                            <button
                            className="edit-button"
                            onMouseEnter={() => setIsHover(true)}
                            onMouseLeave={() => { setIsHover(false); setIsActive(false); }}
                            onMouseDown={() => setIsActive(true)}
                            onMouseUp={() => setIsActive(false)}
                            onClick={handleEditClick}
                            >
                            Edit
                            </button>
                        </div>
                        </div>

                    {/* Classes Section */}
                    <div className="classes-section">
                        <div className="classes-header">
                            <h3 className="classes-title">
                                {userData.firstName?.toUpperCase() || ''}'S Classes
                            </h3>

                            <p className="classes-description">
                                {userData.role === 'Teacher' 
                                    ? `Below are classes that ${userData.firstName?.toUpperCase() || ''} is teaching this semester. This does not include classes they are not teaching.`
                                    : `Below are classes that ${userData.firstName?.toUpperCase() || ''} is taking this semester. This does not include classes they dropped out of.`
                                }
                            </p>
                        </div>

                        {/* Combined Class Cards */}
                        <div className="classes-grid">
                            {userData &&
                                [
                                ...(userData.joinedClasses || []).filter(c => !c.isArchived),
                                ...(userData.classesCreated || [])
                                    .filter(c => !c.isArchived)
                                    .map(c => ({ ...c, createdByYou: true }))
                                ].map((cls, index) => (
                                <div key={index} className="class-card" onClick={() => handleClassClick(cls)}>
                                    <h4>{cls.className || cls.name}</h4>
                                    <div className="class-icon">{cls.createdByYou ? '🧑‍🏫' : '📚'}</div>
                                    {cls.createdByYou && <p className="class-tag">Created by you</p>}
                                </div>
                                ))
                            }

                            {/* Empty grid placeholders to keep layout consistent if items < 3 */}
                            {((userData?.joinedClasses?.length || 0) + (userData?.classesCreated?.length || 0)) % 3 !== 0 &&
                                Array(3 - ((userData?.joinedClasses?.length || 0) + (userData?.classesCreated?.length || 0)) % 3).fill(null).map((_, i) => (
                                <div key={`placeholder-${i}`} className="class-placeholder" />
                                ))
                            }
                            </div>

                    </div>
                </div>
            ) : (
                <p></p>
            )}
        </div>
    );
}

export default Profile;
